import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Car, ArrowLeft, Shield, Lock, Eye, Trash2, UserCheck } from "lucide-react";

const ConfidentialitePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#000091] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-[#000091]" />
            </div>
            <h1 className="text-2xl font-bold">Politique de Confidentialité</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Key Points Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-[#000091]/20 bg-[#000091]/5">
            <CardContent className="p-4 text-center">
              <Shield className="w-8 h-8 text-[#000091] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0F172A]">Données protégées</p>
            </CardContent>
          </Card>
          <Card className="border-[#000091]/20 bg-[#000091]/5">
            <CardContent className="p-4 text-center">
              <Lock className="w-8 h-8 text-[#000091] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0F172A]">Chiffrement SSL</p>
            </CardContent>
          </Card>
          <Card className="border-[#000091]/20 bg-[#000091]/5">
            <CardContent className="p-4 text-center">
              <Eye className="w-8 h-8 text-[#000091] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0F172A]">Transparence</p>
            </CardContent>
          </Card>
          <Card className="border-[#000091]/20 bg-[#000091]/5">
            <CardContent className="p-4 text-center">
              <UserCheck className="w-8 h-8 text-[#000091] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#0F172A]">Conforme RGPD</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-8 prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">1. Introduction</h2>
              <p className="text-slate-600">
                La protection de vos données personnelles est une priorité pour Carte Grise Étrangère. 
                Cette politique de confidentialité vous informe sur la manière dont nous collectons, 
                utilisons et protégeons vos informations personnelles lorsque vous utilisez notre 
                service d'immatriculation de véhicules.
              </p>
              <p className="text-slate-600 mt-4">
                En utilisant notre site, vous acceptez les pratiques décrites dans la présente 
                politique de confidentialité, conformément au Règlement Général sur la Protection 
                des Données (RGPD) et à la loi française « Informatique et Libertés ».
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">2. Responsable du traitement</h2>
              <p className="text-slate-600">
                Le responsable du traitement des données est :<br />
                <strong>Moteur and Co</strong><br />
                6 rue d'Armaillé, 75017 Paris<br />
                Email : dpo@cartegrise-etrangere.fr
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">3. Données collectées</h2>
              <p className="text-slate-600 mb-4">
                Dans le cadre de nos services d'immatriculation, nous collectons les données suivantes :
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-[#0F172A] mb-2">Données d'identité</h4>
                <ul className="text-slate-600 text-sm list-disc list-inside">
                  <li>Nom, prénom, civilité</li>
                  <li>Date et lieu de naissance</li>
                  <li>Adresse postale</li>
                  <li>Numéro de téléphone</li>
                  <li>Adresse email</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-[#0F172A] mb-2">Données du véhicule</h4>
                <ul className="text-slate-600 text-sm list-disc list-inside">
                  <li>Marque, modèle, type</li>
                  <li>Numéro de série (VIN)</li>
                  <li>Date de première immatriculation</li>
                  <li>Pays d'origine</li>
                  <li>Caractéristiques techniques (puissance, énergie)</li>
                </ul>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#0F172A] mb-2">Documents justificatifs</h4>
                <ul className="text-slate-600 text-sm list-disc list-inside">
                  <li>Copie de la pièce d'identité</li>
                  <li>Copie du permis de conduire</li>
                  <li>Justificatif de domicile</li>
                  <li>Carte grise étrangère</li>
                  <li>Autres documents administratifs nécessaires</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">4. Finalités du traitement</h2>
              <p className="text-slate-600 mb-4">
                Vos données personnelles sont collectées et traitées pour les finalités suivantes :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2">
                <li>Traitement de votre demande d'immatriculation auprès de l'ANTS</li>
                <li>Génération des formulaires CERFA obligatoires</li>
                <li>Communication avec vous concernant votre dossier</li>
                <li>Gestion de la facturation et du paiement</li>
                <li>Respect de nos obligations légales et réglementaires</li>
                <li>Amélioration de nos services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">5. Base légale du traitement</h2>
              <p className="text-slate-600">
                Le traitement de vos données est fondé sur :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2 mt-4">
                <li><strong>L'exécution du contrat</strong> : traitement de votre demande d'immatriculation</li>
                <li><strong>L'obligation légale</strong> : conservation des documents pendant la durée légale</li>
                <li><strong>L'intérêt légitime</strong> : amélioration de nos services, sécurité</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">6. Destinataires des données</h2>
              <p className="text-slate-600">
                Vos données peuvent être transmises aux destinataires suivants :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2 mt-4">
                <li><strong>L'ANTS</strong> (Agence Nationale des Titres Sécurisés) pour le traitement de l'immatriculation</li>
                <li><strong>Notre prestataire de paiement</strong> (Stripe) pour la gestion des transactions</li>
                <li><strong>Nos sous-traitants techniques</strong> pour l'hébergement et la maintenance</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Nous ne vendons jamais vos données personnelles à des tiers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">7. Durée de conservation</h2>
              <p className="text-slate-600">
                Vos données personnelles sont conservées pendant les durées suivantes :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2 mt-4">
                <li><strong>Données de dossier</strong> : 5 ans après la finalisation de l'immatriculation</li>
                <li><strong>Documents comptables</strong> : 10 ans (obligation légale)</li>
                <li><strong>Données de compte client</strong> : 3 ans après la dernière activité</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">8. Vos droits</h2>
              <p className="text-slate-600 mb-4">
                Conformément au RGPD, vous disposez des droits suivants :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit d'accès</h4>
                  <p className="text-sm text-slate-600">Obtenir une copie de vos données personnelles</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit de rectification</h4>
                  <p className="text-sm text-slate-600">Corriger des données inexactes ou incomplètes</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit à l'effacement</h4>
                  <p className="text-sm text-slate-600">Demander la suppression de vos données</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit à la portabilité</h4>
                  <p className="text-sm text-slate-600">Recevoir vos données dans un format structuré</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit d'opposition</h4>
                  <p className="text-sm text-slate-600">Vous opposer à certains traitements</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Droit de limitation</h4>
                  <p className="text-sm text-slate-600">Limiter le traitement de vos données</p>
                </div>
              </div>
              <p className="text-slate-600 mt-4">
                Pour exercer ces droits, contactez-nous à : <strong>dpo@cartegrise-etrangere.fr</strong>
              </p>
              <p className="text-slate-600 mt-2">
                Vous pouvez également introduire une réclamation auprès de la CNIL : 
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#000091] hover:underline ml-1">
                  www.cnil.fr
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">9. Sécurité des données</h2>
              <p className="text-slate-600">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour 
                protéger vos données personnelles contre la destruction, la perte, l'altération ou 
                l'accès non autorisé :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2 mt-4">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Stockage sécurisé des données sur des serveurs protégés</li>
                <li>Accès restreint aux données sur la base du besoin d'en connaître</li>
                <li>Audits de sécurité réguliers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">10. Cookies</h2>
              <p className="text-slate-600">
                Notre site utilise des cookies pour améliorer votre expérience de navigation. 
                Les cookies utilisés sont :
              </p>
              <ul className="text-slate-600 list-disc list-inside space-y-2 mt-4">
                <li><strong>Cookies essentiels</strong> : nécessaires au fonctionnement du site</li>
                <li><strong>Cookies de session</strong> : maintien de votre connexion</li>
                <li><strong>Cookies analytiques</strong> : mesure d'audience (anonymisés)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">11. Modifications</h2>
              <p className="text-slate-600">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout 
                moment. Toute modification sera publiée sur cette page avec une date de mise à jour. 
                Nous vous encourageons à consulter régulièrement cette page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-[#000091] mb-4">12. Contact</h2>
              <p className="text-slate-600">
                Pour toute question concernant cette politique de confidentialité ou vos données 
                personnelles, contactez notre Délégué à la Protection des Données :<br /><br />
                <strong>Email :</strong> dpo@cartegrise-etrangere.fr<br />
                <strong>Adresse :</strong> 6 rue d'Armaillé, 75017 Paris
              </p>
            </section>

            <div className="bg-slate-100 rounded-lg p-4 mt-8">
              <p className="text-sm text-slate-500">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ConfidentialitePage;
